// No message bodies in cross-tab storage. Drafts live only in this tab's session.
window.gorentCommunications = (wire, user, agency) => ({
    open: false, opening: false, selected: null, draft: '', key: '', held: false, sending: false, selecting: false,
    pinned: true, notice: '', noticeEntry: null, offline: false, timer: null, busy: false,
    prefix: `gorent:communications:${user}:${agency}:`, observer: null, audio: null,
    init() {
        this.syncDraftIds();
        this.timer = setInterval(() => this.tick(), 1000);
        this.observer = new MutationObserver(() => this.afterUpdate());
        this.observer.observe(this.$root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-send-status'] });
    },
    destroy() { clearInterval(this.timer); this.observer?.disconnect(); this.saveDraft(); },
    readStorage(key) { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } },
    saveDraft() {
        if (!this.selected || window.gorentLoggingOut) return;
        try { sessionStorage.setItem(this.prefix + this.selected, JSON.stringify({ body: this.draft, key: this.key, held: this.held })); } catch { /* private mode */ }
        this.syncDraftIds();
    },
    syncDraftIds() {
        const ids = [];
        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (!key?.startsWith(this.prefix)) continue;
                const id = key.slice(this.prefix.length), value = this.readStorage(key);
                if (/^[1-9]\d*$/.test(id) && (value?.body || value?.held)) ids.push(Number(id));
            }
            wire.set?.('draftThreads', ids, false);
        } catch { /* drafts still usable when storage is unavailable */ }
    },
    async choose(id) {
        if (this.sending || this.selecting) return;
        this.selecting = true;
        this.saveDraft();
        try { await wire.select(id); } catch { this.selecting = false; this.offline = true; return; }
        this.selected = id; this.open = true; this.pinned = true;
        const saved = this.readStorage(this.prefix + id);
        this.draft = saved?.body || ''; this.key = saved?.key || crypto.randomUUID(); this.held = saved?.held || false;
        this.afterUpdate();
        this.selecting = false;
    },
    async back() { if (this.sending) return; this.saveDraft(); this.selected = null; await wire.back(); },
    async follow(id, event, url) {
        this.saveDraft();
        try { await wire.markRead(id, event); location.assign(url); } catch { this.offline = true; }
    },
    async toggle() {
        if (this.opening || this.selecting) return;
        if (this.open) { this.minimize(); return; }
        this.opening = true;
        try { await wire.set('panel', true); this.open = true; this.offline = false; }
        catch { this.offline = true; this.notice = 'No pudimos abrir el chat. Tocá para reintentar.'; this.noticeEntry = null; }
        finally { this.opening = false; }
    },
    minimize() { this.saveDraft(); this.open = false; wire.set('panel', false); this.$nextTick(() => this.$refs.launcher?.focus({ preventScroll: true })); },
    revoke() { this.selected = null; this.draft = ''; this.held = false; this.notice = ''; },
    async tick() {
        if (document.hasFocus() && !document.hidden) {
            try { localStorage.setItem(this.prefix + 'active', JSON.stringify({ id: this.open ? this.selected : null, url: location.pathname + location.search, at: Date.now() })); } catch { /* optional storage */ }
        }
        const seconds = this.open && this.selected ? 3 : 5;
        if (document.hidden || this.busy || Date.now() - (this.lastPoll || 0) < seconds * 1000) return;
        this.lastPoll = Date.now(); this.busy = true;
        try { await wire.poll(this.open && !!this.selected && this.pinned && document.hasFocus()); this.offline = false; }
        catch { this.offline = true; this.notice = ''; this.open = false; }
        finally { this.busy = false; }
    },
    afterUpdate() {
        const history = this.$refs.history;
        if (history && this.pinned && !this.loadingOlder) requestAnimationFrame(() => { history.scrollTop = history.scrollHeight; });
        if (this.key && this.held) {
            const own = [...this.$root.querySelectorAll('[data-send-key]')].find(el => el.dataset.sendKey === this.key);
            if (own && ['sent', 'delivered', 'read'].includes(own.dataset.sendStatus)) this.clearDraft();
        }
    },
    async older() {
        const el = this.$refs.history; const height = el.scrollHeight; const top = el.scrollTop;
        this.loadingOlder = true;
        try { await wire.earlier(); await this.$nextTick(); el.scrollTop = top + el.scrollHeight - height; }
        finally { this.loadingOlder = false; }
    },
    clearDraft() {
        this.draft = ''; this.held = false; this.key = crypto.randomUUID(); this.saveDraft();
    },
    insertSuggestion(text) {
        if (this.sending || this.held || !this.selected) return;
        const next = this.draft.trim() ? `${this.draft}\n\n${text}` : text;
        if (next.length > 1000) { window.alert('El borrador supera los 1000 caracteres. Acortalo antes de agregar la sugerencia.'); return; }
        this.draft = next;
        this.saveDraft();
    },
    newAttempt() {
        if (!window.confirm('¿Comprobaste el estado en el canal? Si el envío anterior quedó incierto, otro envío puede duplicarlo.')) return;
        this.key = crypto.randomUUID(); this.held = false; this.saveDraft();
    },
    async send() {
        if (this.sending || this.held || !this.draft.trim()) return;
        this.sending = true; this.held = true; this.saveDraft();
        try {
            const result = await wire.send(this.draft, this.key, this.selected);
            if (result.confirmed) this.clearDraft();
            // Definitive local validation can be corrected; transport uncertainty stays locked.
            else if (result.status === 'blocked') this.held = false;
        } catch { this.offline = true; }
        finally { this.sending = false; this.saveDraft(); }
    },
    async enableSound(enabled) {
        if (enabled) { this.audio ||= new (window.AudioContext || window.webkitAudioContext)(); await this.audio.resume(); }
        await wire.sound(enabled);
    },
    play() {
        if (this.$root.querySelector('[data-communication-sound]')?.dataset.communicationSound !== '1') return;
        try {
            this.audio ||= new (window.AudioContext || window.webkitAudioContext)();
            if (this.audio.state !== 'running') return;
            const oscillator = this.audio.createOscillator(), gain = this.audio.createGain();
            oscillator.connect(gain); gain.connect(this.audio.destination); gain.gain.value = 0.04;
            oscillator.frequency.value = 660; oscillator.start(); oscillator.stop(this.audio.currentTime + 0.12);
        } catch { /* sound remains optional */ }
    },
    async alerts(entries) {
        if (document.hidden) return;
        if (!navigator.locks && !document.hasFocus()) return;
        const show = async () => {
            const fresh = entries.filter(entry => {
                if (entry.category === 'email' || (this.open && this.selected === entry.id && document.hasFocus())) return false;
                if (document.hasFocus() && new URL(entry.url, location.href).pathname === location.pathname
                    && (entry.channel !== 'WhatsApp' || new URL(entry.url).search === location.search)) return false;
                try {
                    const active = JSON.parse(localStorage.getItem(this.prefix + 'active') || 'null');
                    const target = new URL(entry.url, location.href);
                    if (active && Date.now() - active.at < 4000 && (active.id === entry.id || active.url === target.pathname + target.search)) return false;
                } catch { /* optional storage */ }
                const key = this.prefix + 'alert:' + entry.id;
                try {
                    if (Number(localStorage.getItem(key) || 0) >= entry.event) return false;
                    localStorage.setItem(key, String(entry.event));
                } catch { /* still usable without storage */ }
                return true;
            });
            if (!fresh.length) return;
            this.noticeEntry = fresh[0];
            this.notice = fresh.length === 1 ? `${fresh[0].channel}: nuevo mensaje de ${fresh[0].name}` : `${fresh.length} conversaciones con novedades`;
            this.play();
        };
        if (navigator.locks) await navigator.locks.request(this.prefix + 'alerts', show); else await show();
    },
    async openNotice() {
        const entry = this.noticeEntry; this.notice = '';
        if (entry?.category === 'chats') await this.choose(entry.id);
        else if (entry) await this.follow(entry.id, entry.event, entry.url);
        else await this.toggle();
    },
});

document.addEventListener('submit', event => {
    if (!event.target?.action || new URL(event.target.action, location.href).pathname !== '/logout') return;
    window.gorentLoggingOut = true;
    try { localStorage.setItem('gorent:communications:logout', String(Date.now())); } catch { /* still clear this tab */ }
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('gorent:communications:')) sessionStorage.removeItem(key);
    }
}, true);

window.addEventListener('storage', event => {
    if (event.key !== 'gorent:communications:logout') return;
    window.gorentLoggingOut = true;
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('gorent:communications:')) sessionStorage.removeItem(key);
    }
    document.querySelectorAll('[data-communication-center]').forEach(el => el.remove());
});
